import { computed, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { environment } from '@dxp/core/environment';
import {
    BusinessUnit,
    DefaultTenant,
    IBusinessUnit,
    IQueue,
    IRbacBusinessUnit,
    ITelXLKpiQueueThreshold,
    mergeMissingQueueThresholds,
    NBU_ID,
    TelXLKpiQueueThresholdDefaultState,
} from '@dxp/core/models';
import {
    signalStore,
    withComputed,
    withFeature,
    withHooks,
    withMethods,
    withState,
} from '@ngrx/signals';
import {
    catchError,
    combineLatest,
    distinctUntilChanged,
    filter,
    forkJoin,
    from,
    map,
    mergeMap,
    of,
    switchMap,
    tap,
    toArray,
} from 'rxjs';
import { updateState } from '@angular-architects/ngrx-toolkit';
import { RbacApiService } from '@dxp/core/api';
import { ReloadService } from '@dxp/core/services';
import { QUEUE_STORE } from '../queue/queue.token';
import { withQueues } from '../queue/queue.feature';
import { USER_STORE, withUser } from '../../user';
import {
    businessUnitRecordsEqual,
    toBusinessUnitRecord,
} from './businessunit.helpers';

const KPI_CONCURRENCY = 5;

interface IBusinessUnitStoreState {
    businessUnitIds: Record<string, IRbacBusinessUnit>;
    kpi: Record<string, ITelXLKpiQueueThreshold>;
    loading: boolean;
}

const initialState: IBusinessUnitStoreState = {
    businessUnitIds: {},
    kpi: {},
    loading: true,
};

export const BusinessUnitStore = signalStore(
    { providedIn: 'root' },
    environment.storeWithDevTools('BusinessUnitStore'),
    withFeature(() => withQueues(inject(QUEUE_STORE))),
    withFeature(() => withUser(inject(USER_STORE))),
    withState<IBusinessUnitStoreState>(initialState),
    withComputed(store => ({
        ready: computed<boolean>(() => !store.loading()),
        businessUnits: computed<IBusinessUnit[]>(() => {
            const buRecord = store.businessUnitIds();
            const thresholds = store.kpi();
            const queues = store.queues();
            const buIds = Object.keys(buRecord);
            const queuesByBu: Record<string, IQueue[]> = {};

            for (const q of queues) {
                const buId = q.businessUnit as string;
                (queuesByBu[buId] ??= []).push(q);
            }

            return buIds.map(id => {
                const bu = buRecord[id];
                const threshold =
                    thresholds[id] ?? TelXLKpiQueueThresholdDefaultState;

                return new BusinessUnit(
                    id,
                    bu.name,
                    queuesByBu[id] ?? [],
                    threshold,
                );
            });
        }),
    })),
    withMethods(store => ({
        getBusinessUnitKpi(businessUnitId: string): ITelXLKpiQueueThreshold {
            return (
                store.kpi()[businessUnitId] ??
                TelXLKpiQueueThresholdDefaultState
            );
        },
        updateBusinessUnits(businessUnits: IRbacBusinessUnit[]) {
            updateState(store, 'UPDATE_BUSINESSUNIT_ARRAY', state => {
                const prev = state.businessUnitIds;
                const next = toBusinessUnitRecord(businessUnits);

                if (businessUnitRecordsEqual(prev, next)) {
                    return state;
                }

                return {
                    ...state,
                    businessUnitIds: next,
                };
            });
        },
        updateBusinessUnitKpi(
            businessUnitId: string,
            telXLKpiThreshold: ITelXLKpiQueueThreshold,
        ) {
            mergeMissingQueueThresholds(telXLKpiThreshold);

            updateState(store, 'UPDATE_BUSINESSUNIT_KPI', state => {
                return {
                    ...state,
                    kpi: {
                        ...state.kpi,
                        [businessUnitId]: telXLKpiThreshold,
                    },
                    loading: false,
                };
            });
        },
    })),
    withHooks({
        onInit(
            store,
            rbacApiService = inject(RbacApiService),
            reloadService = inject(ReloadService),
            destroyRef = inject(DestroyRef),
        ) {
            combineLatest(
                toObservable(store.user),
                toObservable(reloadService.refreshTime),
            )
                .pipe(
                    map(([user, refresh]) => ({
                        tenantId: user.tenantId,
                        refresh,
                    })),
                    filter(
                        (x): x is { tenantId: string; refresh: number } =>
                            !!x.tenantId && x.tenantId.length > 0,
                    ),
                    distinctUntilChanged(
                        (a, b) =>
                            a.tenantId === b.tenantId &&
                            a.refresh === b.refresh,
                    ),
                    switchMap(({ tenantId }) =>
                        forkJoin({
                            tenant: rbacApiService.getTenant(tenantId).pipe(
                                catchError(error => {
                                    reloadService.error(
                                        'BUSINESSUNIT_STORE',
                                        error.message,
                                    );

                                    return of(DefaultTenant(tenantId));
                                }),
                            ),
                            businessUnits: rbacApiService
                                .getBusinessUnits()
                                .pipe(
                                    catchError(() => {
                                        return of([]);
                                    }),
                                ),
                        }),
                    ),
                    map(({ tenant, businessUnits }) => {
                        return [
                            ...new Map(
                                businessUnits.map(bu => [bu.id, bu]),
                            ).values(),
                            {
                                id: NBU_ID,
                                name: `${tenant.name} Queues`,
                            },
                        ];
                    }),
                    tap(businessUnits => {
                        store.updateBusinessUnits(businessUnits);
                    }),
                    distinctUntilChanged((prev, next) =>
                        businessUnitRecordsEqual(
                            toBusinessUnitRecord(prev),
                            toBusinessUnitRecord(next),
                        ),
                    ),
                    map(businessUnits => businessUnits.map(bu => bu.id)),
                    switchMap(businessUnitIds => {
                        if (businessUnitIds.length === 0) {
                            return of(
                                [] as {
                                    businessUnitId: string;
                                    kpis?: undefined;
                                }[],
                            );
                        }

                        return from(businessUnitIds).pipe(
                            mergeMap(
                                businessUnitId =>
                                    rbacApiService
                                        .getKpiThresholds(
                                            businessUnitId,
                                            'businessunits',
                                        )
                                        .pipe(
                                            catchError(() => of(undefined)),
                                            map(kpis => ({
                                                businessUnitId,
                                                kpis,
                                            })),
                                        ),
                                KPI_CONCURRENCY,
                            ),
                            toArray(),
                        );
                    }),
                    tap(results => {
                        for (const { businessUnitId, kpis } of results) {
                            store.updateBusinessUnitKpi(
                                businessUnitId,
                                (kpis?.kpiTelXLThreshold as ITelXLKpiQueueThreshold) ??
                                    TelXLKpiQueueThresholdDefaultState,
                            );
                        }
                    }),
                    takeUntilDestroyed(destroyRef),
                )
                .subscribe();
        },
    }),
);
